import type { SortState, SortValue } from '@/lib/tableSort';
import { cn } from '@/lib/utils';
import type { ReactNode } from 'react';

export type TableColumn<T> = {
	key: string;
	header: string;
	align?: 'left' | 'center' | 'right';
	headerClassName?: string;
	cellClassName?: string;
	colClassName?: string;
	render: (row: T) => ReactNode;
	/** Opt-in sorting (TECHENG-1012): the value this column sorts by. Columns without it stay plain. */
	sortValue?: (row: T) => SortValue;
};

type DataTableProps<T> = {
	columns: TableColumn<T>[];
	rows: T[];
	getRowKey: (row: T, index: number) => string;
	className?: string;
	/** The active sort. Rows are sorted by the caller; the table only shows the state. */
	sort?: SortState;
	/** When set, columns with `sortValue` render a clickable header. Omit it and the table is unchanged. */
	onSortChange?: (key: string) => void;
};

const DataTable = <T,>({ columns, rows, getRowKey, className, sort, onSortChange }: DataTableProps<T>) => {
	const defaultTableWrapperClass = 'overflow-x-auto text-[11px] leading-[120%] font-[600] not-italic';
	const defaultTableHeaderCellClass = 'py-1 text-[13px] not-italic font-bold leading-[150%] h-10';
	const defaultTableBodyCellClass = 'py-2';

	const alignToClass = (align?: TableColumn<T>['align']) => {
		switch (align) {
			case 'left':
				return 'text-left';
			case 'right':
				return 'text-right';
			case 'center':
			default:
				return 'text-center';
		}
	};

	return (
		<div className={cn(defaultTableWrapperClass, className)}>
			<table className="w-full table-fixed">
				<colgroup>
					{columns.map((col) => (
						<col key={col.key} className={col.colClassName} />
					))}
				</colgroup>

				<thead>
					<tr className="bg-otnBlack">
						{columns.map((col) => {
							const sortable = !!onSortChange && !!col.sortValue;
							if (!sortable) {
								return (
									<th
										key={col.key}
										scope="col"
										className={cn(defaultTableHeaderCellClass, alignToClass(col.align), col.headerClassName)}
									>
										{col.header}
									</th>
								);
							}
							const active = sort?.key === col.key;
							const direction = active ? sort?.direction : undefined;
							return (
								<th
									key={col.key}
									scope="col"
									aria-sort={direction === 'asc' ? 'ascending' : direction === 'desc' ? 'descending' : 'none'}
									className={cn(defaultTableHeaderCellClass, alignToClass(col.align), col.headerClassName)}
								>
									<button
										type="button"
										onClick={() => onSortChange(col.key)}
										className="hover:text-otnLime focus-visible:outline-otnLime inline-flex cursor-pointer items-center gap-1 focus-visible:outline focus-visible:outline-1"
										title={`Sort by ${col.header}`}
									>
										{col.header}
										<span aria-hidden="true" className={cn('text-[10px]', active ? 'text-otnLime' : 'opacity-30')}>
											{direction === 'desc' ? '▼' : '▲'}
										</span>
									</button>
								</th>
							);
						})}
					</tr>
				</thead>

				<tbody>
					{rows.map((row, rowIndex) => (
						<tr key={getRowKey(row, rowIndex)}>
							{columns.map((col) => (
								<td
									key={col.key}
									className={cn(
										defaultTableBodyCellClass,
										alignToClass(col.align),
										rowIndex === 0 ? '' : 'border-t border-[#333]',
										col.cellClassName,
									)}
								>
									{col.render(row)}
								</td>
							))}
						</tr>
					))}
				</tbody>
			</table>
		</div>
	);
};

export default DataTable;
